import type { Draft } from 'immer';
import { produce } from 'immer';
import type { StateCreator } from 'zustand';
import { create } from 'zustand';
import type {
    Destroy,
    EqualityChecker,
    PartialState,
    StateSelector,
    Subscribe,
} from 'zustand/vanilla';
import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

type StateBaseType = Record<string, any>;
/** @public types */
export type UseSharedSlice<
    State extends StateBaseType,
    Reducers extends ReducerMap<any>,
> = UseStore<State, Reducers>;
/**
 * Represents an action with a payload in a Redux-like manner.
 *
 * @template P The type of the payload.
 *
 * @property type A string that describes the type of action being performed.
 * @property payload The data carried by the action. This can be of any type P.
 *
 * @example
 * // Define an action type
 * type IncrementAction = PayloadAction<number>;
 *
 * const counterReducer = (state: number, action: IncrementAction) => {
 *  return state + action.payload
 * };
 *
 *
 */
export type PayloadAction<P> = {
    type: string;
    payload: P;
};
export interface CreateSharedSliceOptions<
    State extends StateBaseType,
    Reducers extends ReducerMap<State>,
> {
    initialState: State;
    reducers: Reducers;
    name?: string;
    storage?: NamedStateStorage;
}
export type StateWithDispatch<State, Reducers extends ReducerMap<any>> = State & {
    dispatchActions: DispatcherMap<Reducers>;
};
export type ReducerMap<State> = {
    [actionType: string]: PayloadActionReducer<State, any>;
};
export type NamedStateStorage = StateStorage & { name: string };

/** @private types */
type PayloadActionDispatch<P = void> = void extends P ? () => void : (payload: P) => void;
type ReducerWithoutPayload<S> = (state: S) => S;
type PayloadActionReducer<S, P = void> = (
    state: Draft<S>,
    action: PayloadAction<P>,
) => void | S | Draft<S>;
type DispatcherMap<Reducers extends ReducerMap<any>> = {
    [T in keyof Reducers]: Reducers[T] extends ReducerWithoutPayload<any>
        ? PayloadActionDispatch<void>
        : Reducers[T] extends PayloadActionReducer<any, infer P>
          ? PayloadActionDispatch<P>
          : never;
};
interface UseStore<State extends StateBaseType, Reducers extends ReducerMap<any>> {
    (): StateWithDispatch<State, Reducers>;
    <U>(selector: StateSelector<State, U>, equalityFn?: EqualityChecker<U>): U;
    subscribe: Subscribe<State>;
    destroy: Destroy;
    dispatchActions: DispatcherMap<Reducers>;
    getState: () => State;
    setState: CustomSetState<State>;
    /**
     * Resets state to an initial values.
     *
     * Be careful - if initialState is not fully defined (some values are not defined),
     * then those won't be reset. To make it work properly, each value should be defined (even if the value is `undefined`).
     */
    reset: () => void;
}
/** disallow the usage of replace, to avoid the dispatchActions property to be removed */
type CustomSetState<State extends StateBaseType> = (partial: PartialState<State>) => void;

/**
 * Creates a shared state slice with a reducer-like API, using immer for immutable updates.
 * This function provides Flux-pattern state management with minimal boilerplate.
 *
 * @param options - The configuration options for the shared slice.
 * @param options.initialState - The initial state of the slice. Must be a plain object.
 * @param options.reducers - An object containing reducer functions for state updates.
 * @param [options.name] - An optional name for the slice, useful for debugging.
 * @param [options.storage] - An optional storage object for persisting state.
 *
 * @returns A custom hook and an object with imperative methods for state management.
 *
 * @example
 * import { createSharedSlice } from "@luna/state";
 *
 * const useCounter = createSharedSlice({
 *   initialState: { count: 0 },
 *   reducers: {
 *     increment: (state) => { state.count += 1 },
 *     decrement: (state) => { state.count -= 1 },
 *     incrementBy: (state, action: { payload: number }) => {
 *       state.count += action.payload;
 *     },
 *   },
 *   name: 'counter',
 * });
 *
 * // In a React component
 * const CounterComponent = () => {
 *   const { count, dispatchActions } = useCounter();
 *
 *   return (
 *     <div>
 *       <p>Count: {count}</p>
 *       <button onClick={dispatchActions.increment}>Increment</button>
 *       <button onClick={dispatchActions.decrement}>Decrement</button>
 *       <button onClick={() => dispatchActions.incrementBy(5)}>Increment by 5</button>
 *     </div>
 *   );
 * };
 *
 * @example
 * // Using selectors for performance optimization
 * const EfficientCounter = () => {
 *   const count = useCounter(state => state.count);
 *   const { increment } = useCounter(state => state.dispatchActions);
 *
 *   return (
 *     <div>
 *       <p>Count: {count}</p>
 *       <button onClick={increment}>Increment</button>
 *     </div>
 *   );
 * };
 *
 * @example
 * // Usage outside of React (discouraged, use only in exceptional cases)
 * const { getState, dispatchActions, subscribe } = useCounter;
 *
 * console.log(getState().count);
 * dispatchActions.increment();
 * console.log(getState().count);
 *
 * const unsubscribe = subscribe(
 *   state => console.log('New count:', state.count),
 *   state => state.count
 * );
 *
 * // Later: unsubscribe();
 *
 * @remarks
 * - The returned hook must be used for automatic re-renders when the state changes.
 *   Using imperative `getState()` calls is not automatically "reactive" and is discouraged
 *   unless used outside of React or within event handlers.
 * - This function creates a shareable state that lives outside of React.
 * - It allows binding components to only the state they need.
 * - It uses `immer` under the hood to allow simpler reducers.
 * - It provides an escape hatch to allow state reading and subscribes outside of React (for legacy code usage).
 * - For local, non-shareable state within React, use `useLocalSlice()` instead.
 */
export const createSharedSlice = <State extends StateBaseType, Reducers extends ReducerMap<State>>({
    initialState,
    name,
    reducers,
    storage,
}: CreateSharedSliceOptions<State, Reducers>): UseSharedSlice<State, Reducers> => {
    const reducer = (
        baseState: StateWithDispatch<State, Reducers>,
        action: PayloadAction<unknown>,
    ): StateWithDispatch<State, Reducers> =>
        produce<StateWithDispatch<State, Reducers>>(
            baseState,
            (draftState) =>
                reducers[action.type](draftState as Draft<State>, action) as Draft<
                    StateWithDispatch<State, Reducers>
                >,
        );

    const actionTypes = Object.keys(reducers);

    const stateCreator: StateCreator<unknown> = (set) => {
        const dispatch = (args: PayloadAction<unknown>): void =>
            set((state: StateWithDispatch<State, Reducers>) => reducer(state, args));

        const getDispatchActions = (): DispatcherMap<Reducers> => {
            const map: Record<string, (payload: unknown) => void> = {};
            actionTypes.forEach((type) => {
                map[type] = (payload: unknown) => dispatch({ type, payload });
            });

            return map as DispatcherMap<Reducers>;
        };

        return { ...initialState, dispatchActions: getDispatchActions() };
    };

    const withMiddleware = storage
        ? (persist(stateCreator, {
              name: storage.name,
              storage: createJSONStorage(() => storage),
              partialize: (state) =>
                  Object.fromEntries(
                      Object.entries(state as StateWithDispatch<State, Reducers>).filter(
                          ([key]) => key !== 'dispatchActions',
                      ),
                  ),
          }) as StateCreator<unknown>)
        : stateCreator;

    const withDevTools = devtools(withMiddleware, {
        enabled: __DEV__ && import.meta.env?.MODE !== 'test',
        name,
    });

    const useSharedSlice = create(withDevTools) as unknown as UseSharedSlice<State, Reducers>;

    // we expose dispatchActions under a property to allow its usage outside of React.
    useSharedSlice.dispatchActions = useSharedSlice.getState()
        .dispatchActions as DispatcherMap<Reducers>;

    // we save the original getState function for internal usage only.
    const originalZustandGetState = useSharedSlice.getState;

    // we omit the dispatchActions from the getState() return value, to separate actions from state.
    useSharedSlice.getState = () => {
        const { dispatchActions, ...state } = originalZustandGetState();
        return state as State;
    };

    useSharedSlice.reset = () => {
        useSharedSlice.setState(initialState);
    };

    return useSharedSlice as UseSharedSlice<State, Reducers>;
};
